import type { TPlayer } from 'video.js'
import type { IMediaError } from '|>/shared/media-error'

import { Events } from '|>/shared/events'
import { div, insertContent, span } from '|>/shared/h'
import { getXhr } from '|>/shared/utils/vhs'
import { on, register } from '|>/shared/vjs'
import { BaseModalDialog } from '../base'
import { ErrorRetryButton } from '../error-retry-button'
import {
  createCslErrorState,
  handleCslHttpResponse,
  resetCslErrorState,
  resolveCslFromUrls,
} from './lib'

import './error-display.css'

@register
export class ErrorDisplay extends BaseModalDialog {
  title = 'Error'
  codeTitle = 'Error Code'

  // saved error object
  error_?: IMediaError

  // keep track of the title of the content that is being played
  private contentTitle: string = ''

  // keep track of the source URL
  @on(Events.Media.Load, (src: Events.Media.Load) => src?.src)
  private sourceUrl?: string

  declare titleEl?: HTMLElement
  declare descriptionEl?: HTMLElement
  declare codeTitleEl?: HTMLElement
  declare codeEl?: HTMLElement

  // created once; its element is re-inserted into the modal content on every fill()
  private retryBtn_?: ErrorRetryButton

  // suppress error overlay while smart-restart auto-retry is in progress
  private isSmartRestarting: boolean = false

  private cslErrorState = createCslErrorState()

  constructor(player: TPlayer, options) {
    super(player, options)
    this.retryBtn_ = new ErrorRetryButton(player, {})
    this.descEl_.remove()

    this.responseHook = this.responseHook.bind(this)

    // manually triggered player error
    // it will be handled by the hook, and then 'error' event will be triggered bellow
    this.player_.on(Events.Media.Error, (_, error: Events.Media.Error) => {
      this.player_.error(error)
    })

    // triggered by player error
    // as well as manually triggered player error above
    this.player_.on('error', () => {
      this.showError(this.player_.error())
    })

    /* Handle HTTP errors
     *
     * tech_.vhs tied to only one exact tech so 'loadstart' makes shure xhr hook passed
     */
    this.player_.on(['xhr-hooks-ready', 'loadstart'], () => {
      // hook to intercept internal xhr responses
      const xhr = getXhr(this.player_, 'Error display')
      const onResponse = xhr?.onResponse
      const offResponse = xhr?.offResponse

      if (offResponse) offResponse(this.responseHook)
      if (onResponse) onResponse(this.responseHook)
    })

    // TODO: not sure if this works at all, need to investigate more
    // triggered by player tech error
    // this.player_.tech_.on('error', () => {
    //   this.showError(this.player_.tech_.error_)
    // })
  }

  showError(error: IMediaError) {
    if (!error) return
    this.error_ = error
    this.player_.pause()
    this.player_.resetSoft()
    if (this.isSmartRestarting) return // spinner is showing; no overlay during auto-retry
    this.addErrorClass()
    this.open()
    this.fillError()
  }

  /** Auto-retry step — close overlay, show loading spinner; suppress further showError() calls. */
  @on(Events.Player.SmartRestartRetrying)
  handleSmartRestartRetrying() {
    this.isSmartRestarting = true
    this.close()
    this.removeErrorClass()
    this.addSmartRestartClass()
  }

  /** Manual retries step — clear retry mode and show the last saved error with the Retry button. */
  @on(Events.Player.SmartRestartError)
  handleSmartRestartExhausted() {
    this.showSavedErrorOverlay()
  }

  /** Retry succeeded — clear retry mode and any residual waiting state. */
  @on(Events.Player.SmartRestartSuccess)
  handleSmartRestartSuccess() {
    this.isSmartRestarting = false
    this.removeSmartRestartClass()
  }

  /**
   * Auto-retry aborted by a no-retry error (FP-4176) — clear spinner and show
   * the saved overlay without the Smart-Restart Retry button.
   */
  @on(Events.Player.SmartRestartAborted)
  handleSmartRestartAborted() {
    this.showSavedErrorOverlay()
  }

  private showSavedErrorOverlay(): void {
    this.isSmartRestarting = false
    this.removeSmartRestartClass()
    if (this.error_) {
      this.addErrorClass()
      this.open()
      this.fillError()
    }
  }

  @on(Events.Media.Load)
  hideError(source?: Events.Media.Load) {
    // A sourceless Media.Load means the current source was cleared, not that new
    // content is loading. Clearing the source must not tear down an active error
    // overlay (e.g. a CSL 433 whose playback URL gets blanked right after the error).
    // Real retries and new sources always carry a src and proceed as before; genuine
    // content switches are handled by onNextContentSelected (ContentChanged).
    if (!source?.src) return

    if (!this.isSmartRestarting) this.error_ = undefined // preserve error across retries
    this.close()
    this.removeErrorClass()
    this.removeClass('error-display--csl')
    // Allow next 433 to be detected (e.g. on the in-flight retry).
    // The retry button's own Load handler hides it while keeping isRetrying.
    resetCslErrorState(this.cslErrorState)
  }

  @on(Events.Media.ContentChanged)
  onNextContentSelected() {
    // this clears error synced with new content data, works with all ways content change
    // not delayed as Events.Media.Load
    this.isSmartRestarting = false
    this.removeSmartRestartClass()
    this.error_ = undefined
    this.empty()
    this.close()
    this.removeErrorClass()
    this.removeClass('error-display--csl')
    resetCslErrorState(this.cslErrorState)
  }

  responseHook(request, _error, response) {
    // update expired token
    if (response?.statusCode === 403) {
      this.player_.trigger(Events.Player.TokenExpired)
    }

    if (
      handleCslHttpResponse(
        this.player_,
        request,
        response,
        this.sourceUrl,
        this.cslErrorState
      )
    ) {
      return
    }

    // ignore requests other than the current playback url (e.g. chunks)
    if (this.sourceUrl !== request.url) return

    // trigger special player error for some HTTP errors
    if (
      [
        400, // BAD_REQUEST
        402, // PAYMENT_REQUIRED
        403, // ACCESS_ERROR
        404, // NOT_FOUND
        451, // UNAVAILABLE_FOR_LEGAL_REASONS
        500, // API_ERROR
      ].includes(response?.statusCode)
    ) {
      this.player_.error({
        code: response.statusCode,
        metadata: {
          requestUrl: request.url,
          statusCode: response.statusCode,
        },
      })
    }
  }

  addErrorClass() {
    this.player_.addClass('vjs-error')
  }

  removeErrorClass() {
    this.player_.removeClass('vjs-error')
  }

  addSmartRestartClass() {
    this.player_.addClass('vjs-x-smart-restarting')
    this.player_.addClass('vjs-waiting')
  }

  removeSmartRestartClass() {
    this.player_.removeClass('vjs-x-smart-restarting')
    this.player_.removeClass('vjs-waiting')
  }

  override close() {
    if (!this.player_.tech_) return

    super.close()
  }

  override content() {
    const elements: Element[] = [
      (this.titleEl = div('.error-title', this.localize(this.title))),
      (this.descriptionEl = div('.error-description')),
      div(
        '.error-code',
        (this.codeTitleEl = span('.error-code-title')),
        (this.codeEl = span('.error-code-name'))
      ),
    ]
    if (this.retryBtn_) elements.push(this.retryBtn_.el())
    this.fillError()
    return elements
  }

  /**
   * Fill content elements with localized strings
   */
  fillError() {
    const error = this.error_
    if (!error) return

    let title = 'title' in error ? error.title || this.title : this.title
    let message = error.message || ''
    let code = error.code

    const isCsl = error.originalCode === 433
    const metadata = error.metadata as
      | Record<string, unknown>
      | null
      | undefined
    const csl = isCsl
      ? ((metadata?.csl as number | undefined) ??
        this.cslErrorState.knownCsl ??
        resolveCslFromUrls(this.sourceUrl, metadata?.src as string | undefined))
      : undefined

    // localize using values
    title = this.localize(title) // "Error" string by default
    if (isCsl) {
      if (csl !== undefined) this.cslErrorState.knownCsl = csl
      message = this.localize(message, [String(csl ?? 1)])
    } else {
      message = this.localize(message, [
        this.contentTitle ? `"${this.contentTitle}"` : 'content', // replace positional placeholder "{1}" with current content title
      ])
    }
    let codeTitle = this.localize(this.codeTitle) // "Error Code" string

    // fill elements with localized strings
    if (this.titleEl) insertContent(this.titleEl, `${title}`)
    if (this.descriptionEl) insertContent(this.descriptionEl, `${message}`)
    if (this.codeTitleEl) insertContent(this.codeTitleEl, `${codeTitle}`)
    if (this.codeEl) insertContent(this.codeEl, `${code}`)

    // apply CSL-specific dialog class (hides error code block, adjusts title colour)
    if (isCsl) {
      this.addClass('error-display--csl')
    } else {
      this.removeClass('error-display--csl')
    }
  }

  /**
   * Keep track of the title of the content that is being played
   * and re-fill error with updated content title
   */
  @on(Events.Media.TitleChanged)
  updateContentTitle(title: Events.Media.TitleChanged) {
    this.contentTitle = title.title || ''
    this.fillError()
  }

  /**
   * Re-fill error on language change
   */
  override handleLanguagechange() {
    this.fillError()
  }

  override dispose() {
    this.retryBtn_?.dispose()
    this.retryBtn_ = undefined
    super.dispose()
  }
}

// Default values
ErrorDisplay.options = {
  className: 'vjs-x-error-display',
  pauseOnOpen: true,
  fillAlways: true,
  temporary: false,
  uncloseable: true,
}
